import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useCreatePartGroup } from "../services/PartGroupService";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";

interface PartGroupCreateModalProps {
  open: boolean;
  onClose: () => void;
}

const PartGroupCreateModal = ({ open, onClose }: PartGroupCreateModalProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const createGroup = useCreatePartGroup();

  const handleSubmit = () => {
    if (!name.trim()) return;
    createGroup.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        // Part groups are globally visible; no per-group visibility choice.
        visibility_level: "public",
      },
      {
        onSuccess: () => {
          handleReset();
          onClose();
        },
        onError: (err) => {
          const detail = extractErrorDetail(err);
          console.error("Failed to create part group", err);
          NucToast({
            message: `Failed to create part group: ${detail}`,
            type: "error",
            hideProgressBar: false,
          });
        },
      },
    );
  };

  const handleReset = () => {
    setName("");
    setDescription("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={createGroup.isLoading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create Part Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Group Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createGroup.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || createGroup.isLoading}
          startIcon={
            createGroup.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {createGroup.isLoading ? "Creating..." : "Create Group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartGroupCreateModal;
